import React, { useState, useEffect } from "react";
import { X, Flame } from "lucide-react";
import { useApp } from "../AppContext";

const STORAGE_KEY = "launch-banner-dismissed-v1";

const AnnouncementBanner = () => {
    const { lang, branding } = useApp();
    const [visible, setVisible] = useState(false);

    // Custom admin-set message takes priority; if no custom message and not enabled, hide.
    const customMessage =
        lang === "es" ? branding.announcement_text_es : branding.announcement_text_en;
    const adminEnabled = !!branding.announcement_enabled;

    useEffect(() => {
        try {
            if (!adminEnabled) {
                setVisible(false);
                return;
            }
            if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
        } catch {
            setVisible(adminEnabled);
        }
    }, [adminEnabled, customMessage]);

    // Set CSS variable so the fixed header can sit below the banner
    useEffect(() => {
        document.documentElement.style.setProperty(
            "--banner-h",
            visible ? "52px" : "0px"
        );
    }, [visible]);

    const dismiss = () => {
        try {
            localStorage.setItem(STORAGE_KEY, "1");
        } catch {}
        setVisible(false);
    };

    if (!visible) return null;

    const defaultMessage =
        lang === "es" ? (
            <>
                <b>¡Estamos calentando los fogones!</b> 🔥 Por ahora pedimos{" "}
                <b className="underline decoration-2 underline-offset-2">48 horas</b> de
                aviso — cocinamos con paciencia caribeña ☀️. Muy pronto
                tendrás cada delicia en tu puerta más rápido que un merengue.{" "}
                <span className="opacity-90">¡Gracias por la paciencia, mi gente!</span>
            </>
        ) : (
            <>
                <b>Our stoves are still warming up!</b> 🔥 For now we ask for{" "}
                <b className="underline decoration-2 underline-offset-2">48 hours</b>{" "}
                notice — we cook with that good Caribbean patience ☀️.
                Very soon you'll have every delicacy at your door faster than a
                merengue beat.{" "}
                <span className="opacity-90">Thanks for bearing with us, mi gente!</span>
            </>
        );

    const message = customMessage ? customMessage : defaultMessage;

    return (
        <div
            data-testid="launch-banner"
            className="fixed top-0 left-0 right-0 z-[60] text-white shadow-lg"
            style={{
                background: `linear-gradient(90deg, var(--flag-1) 0%, var(--brand-primary) 50%, var(--flag-3) 100%)`,
            }}
        >
            {/* Subtle moving stripe */}
            <div
                aria-hidden
                className="absolute inset-0 opacity-20 pointer-events-none"
                style={{
                    background:
                        "repeating-linear-gradient(45deg, transparent 0px, transparent 12px, rgba(255,255,255,0.08) 12px, rgba(255,255,255,0.08) 24px)",
                }}
            />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-2.5 sm:py-3 flex items-center gap-3">
                <div className="hidden sm:flex w-9 h-9 rounded-full bg-white/15 backdrop-blur items-center justify-center shrink-0">
                    <Flame className="w-4 h-4" />
                </div>
                <p className="flex-1 text-xs sm:text-sm leading-snug">{message}</p>
                <button
                    onClick={dismiss}
                    data-testid="banner-dismiss-btn"
                    aria-label="dismiss"
                    className="w-8 h-8 shrink-0 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

export default AnnouncementBanner;
